import React, { useEffect, useState, useCallback } from 'react';
import { Head, Link } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { OrderSuccessData, OrderSuccessRoutes } from '../../types/orderSuccess';
import { dataLayerPushAsync, trackFirstTimePurchase } from '../../utils/tracking';

interface ReferralGiftInfo {
  product_name: string;
  format_name: string;
  content_description: string;
  quantity: number;
  image_url: string;
  webp_image_url: string;
  has_image: boolean;
  price: number;
  description: string;
}

interface ReferralOffer {
  code: string;
  share_url: string;
  gift: ReferralGiftInfo;
  reward_referrer: boolean;
}

interface BarrioCampaignShareData {
  shareUrl: string;
  rewardAmount: number;
  neighborhood: string;
  discountPercent: number;
}

interface OrderSuccessProps {
  order?: OrderSuccessData;
  referralOffer?: ReferralOffer | null;
  barrioCampaignShare?: BarrioCampaignShareData | null;
  routes: OrderSuccessRoutes;
}

declare global {
  interface Window {
    dataLayer: Array<Record<string, any>>;
  }
}

export default function OrderSuccess({ order, referralOffer, barrioCampaignShare, routes }: OrderSuccessProps) {
  useEffect(() => {
    if (order && typeof window !== 'undefined') {
      // Una sola conversión por orden: un re-render o volver a esta página en
      // la SPA no debe duplicar el purchase (#92). sessionStorage y no un ref
      // porque la duplicación real es re-montar el componente.
      const purchaseKey = `purchase_tracked_${order.id}`;
      let alreadyTracked = false;
      try {
        alreadyTracked = sessionStorage.getItem(purchaseKey) === '1';
      } catch {
        // storage bloqueado: mejor arriesgar un duplicado que perder la conversión
      }

      if (window.dataLayer && !alreadyTracked) {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'purchase',
          // Mismo event_id que manda el servidor por Meta CAPI (#94): Meta
          // deduplica por event_name + event_id. Determinístico a propósito.
          event_id: `purchase_${order.id}`,
          ecommerce: order.google_analytics_purchase,
        });
        try {
          sessionStorage.setItem(purchaseKey, '1');
        } catch {
          // sin storage no hay marca, pero el push ya salió
        }

        if (order.is_first_purchase) {
          trackFirstTimePurchase({
            transaction_id: order.id,
            value: order.total,
          });
        }
      }
    }
  }, [order]);

  return (
    <MainLayout>
      <Head>
        <title>Pedido Confirmado | Abasto Virtual</title>
        <meta name="description" content="Tu pedido ha sido confirmado" />
        <meta name="robots" content="noindex" />
      </Head>

      <div className="min-h-screen bg-gradient-to-br from-green-50 to-slate-50 pb-16 pt-10 md:pt-16">
        <div className="mx-auto max-w-[600px] px-4">
          <div className="mt-6 rounded-2xl bg-white p-6 text-center shadow-lg md:p-8">
            {/* Success icon */}
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                className="h-10 w-10 text-green-600"
              >
                <path
                  d="M20 6L9 17l-5-5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h1 className="mb-2 text-2xl font-bold text-green-800 md:text-[26px]">
              Recibimos tu pedido!
            </h1>
            <p className="mb-8 text-base text-gray-600">
              Estamos preparando tu pedido con mucho cuidado
            </p>

            {order && (
              <>
                <p className="mb-4 text-[13px] text-gray-400">
                  Pedido #{order.id}
                </p>

                {/* Delivery highlight */}
                <div className="mb-6 rounded-xl border border-yellow-300 bg-yellow-50 p-4">
                  <div className="mb-2 flex items-center justify-center gap-2 text-sm font-semibold text-yellow-900">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="3" width="15" height="13" rx="2" ry="2" />
                      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                      <circle cx="5.5" cy="18.5" r="2.5" />
                      <circle cx="18.5" cy="18.5" r="2.5" />
                    </svg>
                    Fecha de entrega
                  </div>
                  <p className="text-base font-semibold capitalize text-yellow-900">
                    {order.human_friendly_delivery_date}
                  </p>
                </div>

                {/* Order details */}
                <div className="mb-6 rounded-xl bg-slate-50 p-5 text-left">
                  <div className="flex items-start justify-between border-b border-gray-200 pb-3">
                    <span className="text-sm text-gray-600">Dirección</span>
                    <span className="text-right text-sm font-medium text-gray-900">
                      {order.address.split(',')[0]}
                    </span>
                  </div>
                  <div className="flex items-start justify-between border-b border-gray-200 py-3">
                    <span className="text-sm text-gray-600">Forma de pago</span>
                    <span className="text-right text-sm font-medium text-gray-900">
                      {order.payment_method_name}
                    </span>
                  </div>
                  <div className="flex items-start justify-between pt-3">
                    <span className="text-sm text-gray-600">Total</span>
                    <span className="text-right text-base font-medium text-green-600">
                      ${order.total.toLocaleString('es-AR')}
                    </span>
                  </div>
                </div>
              </>
            )}

            {/* Referral offer or barrio campaign share — inline, before actions */}
            {barrioCampaignShare ? (
              <BarrioCampaignShareBanner share={barrioCampaignShare} />
            ) : referralOffer ? (
              <ReferralBanner offer={referralOffer} />
            ) : null}

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <Link
                href={routes.orders}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-green-700 hover:no-underline"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
                Ver mis pedidos
              </Link>
              <Link
                href={routes.products}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-gray-100 px-6 py-3.5 text-base font-semibold text-gray-700 transition-colors hover:bg-gray-200 hover:text-gray-700 hover:no-underline"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                Volver a la tienda
              </Link>
            </div>

            {/* Footer */}
            <div className="mt-8 border-t border-gray-200 pt-6">
              <p className="m-0 text-[13px] text-gray-400">
                Si tenés alguna duda, contactános por el botón de chat de la
                página,{' '}
                <a
                  href="https://wa.me/5493512026384"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-600 hover:underline"
                >
                  WhatsApp
                </a>{' '}
                o escribinos a info@abastovirtual.com
              </p>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

// ── Referral Banner (inline within the card) ──

function ReferralBanner({ offer }: { offer: ReferralOffer }) {
  const [copied, setCopied] = useState(false);

  // Track banner impression on mount
  useEffect(() => {
    dataLayerPushAsync({
      event: 'referral_banner_viewed',
      referral_code: offer.code,
    });
  }, []);

  const trackShare = (method: 'native' | 'clipboard') => {
    dataLayerPushAsync({
      event: 'referral_shared',
      referral_code: offer.code,
      share_method: method,
    });
  };

  const handleShare = useCallback(async () => {
    const shareData = {
      title: 'Te regalo algo de Abasto Virtual',
      text: `Te quiero regalar ${offer.gift.description} GRATIS en Abasto Virtual. Probalo con este link:`,
      url: offer.share_url,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        trackShare('native');
        return;
      } catch (err: any) {
        if (err.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(offer.share_url);
      setCopied(true);
      trackShare('clipboard');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      alert(offer.share_url);
    }
  }, [offer]);

  return (
    <div className="mb-6 overflow-hidden rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 p-5 text-left">
      <div className="flex items-start gap-4">
        {/* Product image */}
        {offer.gift.has_image && (
          <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-xl bg-white/90 p-1.5">
            <picture>
              <source srcSet={offer.gift.webp_image_url} type="image/webp" />
              <img
                src={offer.gift.image_url}
                alt={offer.gift.product_name}
                className="h-full w-full object-contain"
              />
            </picture>
          </div>
        )}

        <div className="min-w-0 flex-1">
          <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-white/80">
            Regalale a un amigo
          </p>
          <p className="mb-1 text-[15px] font-bold leading-snug text-white">
            {offer.gift.description} GRATIS
          </p>
          <p className="text-xs leading-relaxed text-white/80">
            Compartí tu link y cuando haga su primer pedido, lo recibe gratis.
            {offer.reward_referrer && (
              <strong className="text-white"> Vos también recibís el mismo regalo.</strong>
            )}
          </p>
        </div>
      </div>

      <button
        onClick={handleShare}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-bold text-amber-700 transition-colors hover:bg-amber-50"
      >
        {copied ? (
          <>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Link copiado!
          </>
        ) : (
          <>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            Compartir link
          </>
        )}
      </button>
    </div>
  );
}

// ── Barrio Campaign Share Banner ──

function BarrioCampaignShareBanner({ share }: { share: BarrioCampaignShareData }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    dataLayerPushAsync({
      event: 'barrio_share_banner_viewed',
      neighborhood: share.neighborhood,
    });
  }, []);

  return (
    <div className="mb-6 overflow-hidden rounded-xl border border-green-200 bg-green-50 p-5 text-left">
      <div className="mb-3">
        <div className="mb-1 text-sm font-bold text-gray-800">
          🎁 Compartí y ganá ${share.rewardAmount.toLocaleString('es-AR')} de crédito
        </div>
        <p className="text-[13px] text-gray-500">
          Por cada vecino de {share.neighborhood} que pida con tu link, ganás crédito para tu próxima compra.
        </p>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => {
            const text = `Yo pido verdura y fruta por acá y me llega a ${share.neighborhood}. Si querés probar, con este link tenés ${Math.round(Number(share.discountPercent))}% off:\n${share.shareUrl}`;
            window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
            dataLayerPushAsync({ event: 'barrio_share_whatsapp', neighborhood: share.neighborhood });
          }}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-[#20bd5a]"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.126.553 4.12 1.52 5.855L.06 23.498l5.791-1.52A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-1.97 0-3.837-.53-5.445-1.454l-.39-.232-3.436.902.917-3.349-.254-.403A9.694 9.694 0 012.25 12c0-5.385 4.365-9.75 9.75-9.75S21.75 6.615 21.75 12s-4.365 9.75-9.75 9.75z" />
          </svg>
          WhatsApp
        </button>
        <button
          type="button"
          onClick={async () => {
            const shareText = `Yo pido verdura y fruta por acá y me llega a ${share.neighborhood}. Si querés probar, con este link tenés ${Math.round(Number(share.discountPercent))}% off:`;
            if (navigator.share) {
              try {
                await navigator.share({ title: 'Descuento Abasto Virtual', text: shareText, url: share.shareUrl });
                dataLayerPushAsync({ event: 'barrio_share_native', neighborhood: share.neighborhood });
                return;
              } catch (err: any) {
                if (err.name === 'AbortError') return;
              }
            }
            try {
              await navigator.clipboard.writeText(share.shareUrl);
              setCopied(true);
              setTimeout(() => setCopied(false), 2500);
              dataLayerPushAsync({ event: 'barrio_share_copy', neighborhood: share.neighborhood });
            } catch {
              alert(share.shareUrl);
            }
          }}
          className={`flex flex-1 items-center justify-center gap-2 rounded-lg border-2 px-4 py-2.5 text-sm font-medium transition-all ${
            copied
              ? 'border-green-500 bg-green-100 text-green-700'
              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
          }`}
        >
          {copied ? '¡Copiado!' : 'Compartir'}
        </button>
      </div>
    </div>
  );
}
